import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, SyntheticEvent, useEffect, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useProductWishList } from '@src/hooks/useProductWishlist'
import { IconButton } from '@src/quince-components/src'
import { getAddToCartTrackingDetails } from '@src/utils/functions/utils'

import HeartIcon from '../../icons/heartIcon.inline.svg'
import { CardVariant, IWebsiteProduct } from '../../product-card.types'

import styles from './add-to-wishlist.module.scss'

export const ProductCardAddToWishlist: FC<{
  productCard: { product: IWebsiteProduct }
  spidWishlist: string | number
  selectedCardVariant?: CardVariant
  wishlistIconClassName?: string
  wishlistTrackingConfig: Record<string, unknown>
  wishlistIconClass?: string
}> = props => {
  const { t } = useTranslation('common')
  const [isWishListed, setIsWishListed] = useState(false)
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useProductWishList()

  const selectedColor = props.selectedCardVariant?.displayConfig.value

  useEffect(() => {
    setIsWishListed(isItemInWishList(props.spidWishlist as number, selectedColor ?? ''))
  }, [isItemInWishList, props.spidWishlist, selectedColor])

  const onWishListToggle = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsWishListed(p => !p)
    if (isWishListed) {
      removeItemFromWishList(props.spidWishlist as number, selectedColor ?? '', props.wishlistTrackingConfig).catch(
        (e: unknown) => {
          console.error(e)
        },
      )
    } else {
      const productCard = props.productCard.product

      addItemToWishList(
        props.spidWishlist as number,
        selectedColor ?? '',
        getAddToCartTrackingDetails(productCard, selectedColor, props.wishlistTrackingConfig),
      ).catch((e: unknown) => {
        console.error(e)
      })
    }
  }

  const productTitle = props.productCard.product.title
  const colorName = props.selectedCardVariant?.displayConfig.displayName
  const productName = colorName ? `${colorName} ${productTitle}` : productTitle
  const wishlistAriaLabel = isWishListed
    ? t('pc_remove_wishlist_aria', { productName })
    : t('pc_add_wishlist_aria', { productName })

  return (
    <IconButton
      className={classnames(props.wishlistIconClassName, styles.button)}
      iconClassName={classnames(
        styles.icon,
        isWishListed ? styles.blackIcon : styles.whiteIcon,
        props.wishlistIconClass,
      )}
      iconSvg={HeartIcon}
      onClick={onWishListToggle}
      ariaLabel={wishlistAriaLabel}
      dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
      aria-pressed={isWishListed}
    />
  )
}
